import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {useChangePatientStatus} from '@/features/patients/services/useChangePatientStatus'
import {PatientExtended} from '@/features/patients/types'
import {QUERY_KEYS} from '@/queryClient'
import {Trans, useTranslation} from '@/translations/i18n'
import {errorHandler} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import {useQueryClient} from '@tanstack/react-query'

/**
 * Confirmation step for the discharge/readmission actions triggered from PatientDischargeMenuItem.
 * It lives outside the options dropdown so that the menu can close on click without unmounting it.
 */
export const PatientDischargeDialog = ({
    patient,
    dialogStore
}: {
    patient: PatientExtended
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const isDischarged = patient.status == 'inactive'

    const changePatientStatusMutation = useChangePatientStatus({
        urlParams: {patientId: patient.id},
        params: {responseType: 'extended'},
        options: {
            onError: errorHandler,
            onSuccess: () => {
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS]})
                toast.success(isDischarged ? t('patients:readmit_success') : t('patients:discharge_success'))
                dialogStore.setOpen(false)
            }
        }
    })

    return (
        <ConfirmationDialog
            dialogStore={dialogStore}
            onConfirm={() => changePatientStatusMutation.mutate({status: isDischarged ? 'active' : 'inactive'})}
            isConfirmPending={changePatientStatusMutation.isPending}
            hideDialogBody
            headerSlot={
                <Flexbox direction="column" gap={2} fullWidth>
                    <DialogHeading>
                        {isDischarged ? t('patients:readmit_patient') : t('patients:discharge_patient')}
                    </DialogHeading>
                    <Text palette="neutral" shade="600" size="textSm">
                        {t('commons:patient_fullname_value', {fullName: patient.fullName})}
                    </Text>
                </Flexbox>
            }
            contentSlot={
                <Text palette="neutral" shade="950" size="textSm">
                    <Trans
                        i18nKey={
                            isDischarged ? 'patients:readmit_patient_confirm' : 'patients:discharge_patient_confirm'
                        }
                        values={{fullName: patient.fullName}}
                        components={{b: <b />}}
                    />
                </Text>
            }
        />
    )
}
